const getRoomLabel = (itemOrRoom) => {
  if (!itemOrRoom) return null;
  return (
    itemOrRoom.allocated_room_number ||
    itemOrRoom.allocated_room?.room_number ||
    itemOrRoom.allocated_room?.number ||
    itemOrRoom.room_number ||
    (itemOrRoom.allocated_room_id ? `Room ${itemOrRoom.allocated_room_id}` : null) ||
    (itemOrRoom.room_id ? `Room ${itemOrRoom.room_id}` : null)
  );
};

const normalizeRoomText = (value) =>
  String(value || '')
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .replace(/^Room\s+/i, '')
    .trim();

const getDateKey = (item) => {
  if (!item?.date) return '';
  return String(item.date).slice(0, 10);
};

const getTypeId = (item) => Number((item?.room_type_id ?? item?.room_type?.id) || 0);

const isCheckedOutItem = (item) => {
  const raw = item?.checkout;
  if (typeof raw === 'string') {
    return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
  }
  return raw === true || raw === 1;
};

const buildUpgradeLinkChips = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  const sortedByDate = [...items].sort((a, b) => {
    const dateA = getDateKey(a);
    const dateB = getDateKey(b);
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    const seqA = Number(a?.room_sequence || 1);
    const seqB = Number(b?.room_sequence || 1);
    if (seqA !== seqB) return seqA - seqB;
    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  });

  const upgradeLinkMap = new Map();
  sortedByDate.forEach((item) => {
    const upgradeFromTypeId = Number(
      (item?.upgraded_from_room_type_id ?? item?.upgraded_from_room_type?.id) || 0
    );
    if (!upgradeFromTypeId) return;
    const itemDate = getDateKey(item);
    const toRoom = getRoomLabel(item);
    if (!itemDate || !toRoom) return;

    const seq = Number(item?.room_sequence || 1);
    const previousItem = [...sortedByDate]
      .reverse()
      .find((prev) => {
        const prevDate = getDateKey(prev);
        const prevRoom = getRoomLabel(prev);
        if (!prevDate || !prevRoom || prevDate >= itemDate) return false;
        const prevTypeId = getTypeId(prev);
        if (prevTypeId && prevTypeId === upgradeFromTypeId) return true;
        return Number(prev?.room_sequence || 1) === seq;
      });
    const fromRoom = previousItem ? getRoomLabel(previousItem) : null;
    if (!fromRoom || fromRoom === toRoom) return;

    const linkLabel = `[${normalizeRoomText(fromRoom)}->${normalizeRoomText(toRoom)}]`;
    if (!upgradeLinkMap.has(linkLabel)) {
      upgradeLinkMap.set(linkLabel, {
        key: `upgrade_link_${seq}_${linkLabel}`,
        label: linkLabel,
      });
    }
  });

  if (upgradeLinkMap.size === 0) {
    const checkedOutCandidates = sortedByDate.filter((it) => isCheckedOutItem(it) && getRoomLabel(it) && getDateKey(it));
    const activeCandidates = sortedByDate.filter((it) => !isCheckedOutItem(it) && getRoomLabel(it) && getDateKey(it));
    const fromItem = [...checkedOutCandidates].sort((a, b) => getDateKey(b).localeCompare(getDateKey(a)))[0];
    const toItem = [...activeCandidates].sort((a, b) => getDateKey(a).localeCompare(getDateKey(b)))[0];
    const fromRoom = fromItem ? getRoomLabel(fromItem) : null;
    const toRoom = toItem ? getRoomLabel(toItem) : null;
    if (fromRoom && toRoom && fromRoom !== toRoom) {
      const fallbackLabel = `[${normalizeRoomText(fromRoom)}->${normalizeRoomText(toRoom)}]`;
      upgradeLinkMap.set(fallbackLabel, {
        key: `upgrade_link_fallback_${fallbackLabel}`,
        label: fallbackLabel,
      });
    }
  }

  return Array.from(upgradeLinkMap.values());
};

export const buildBookingRoomChips = (booking) => {
  const items = Array.isArray(booking?.booking_items) ? booking.booking_items : [];

  if (items.length > 0) {
    const sortedItems = [...items].sort((a, b) => {
      const seqA = Number(a?.room_sequence) || 1;
      const seqB = Number(b?.room_sequence) || 1;
      if (seqA !== seqB) return seqA - seqB;

      const rtA = Number(a?.room_type_id) || 0;
      const rtB = Number(b?.room_type_id) || 0;
      if (rtA !== rtB) return rtA - rtB;

      const dateA = a?.date || '';
      const dateB = b?.date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);

      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });

    const sequenceMap = new Map();
    sortedItems.forEach((item) => {
      const roomLabel = getRoomLabel(item);
      if (!roomLabel) return;

      const sequence = Number(item?.room_sequence) || 1;
      const roomTypeId = Number(item?.room_type_id) || 0;
      const key = `${roomTypeId}_${sequence}`;
      const dateKey = item?.date || '';

      if (!sequenceMap.has(key)) {
        sequenceMap.set(key, {
          key,
          sequence,
          roomTypeId,
          rooms: [roomLabel],
          roomItems: [{ roomLabel, dateKey }],
        });
        return;
      }

      const entry = sequenceMap.get(key);
      entry.roomItems.push({ roomLabel, dateKey });
      if (entry.rooms[entry.rooms.length - 1] !== roomLabel) {
        entry.rooms.push(roomLabel);
      }
    });

    const chips = Array.from(sequenceMap.values())
      .sort((a, b) => {
        if (a.sequence !== b.sequence) return a.sequence - b.sequence;
        return a.roomTypeId - b.roomTypeId;
      })
      .flatMap((entry) => {
        // If same date has multiple room labels under same room_type+sequence,
        // split into separate chips (instead of chained "A->B") to avoid confusion.
        const labelsByDate = new Map();
        (entry.roomItems || []).forEach(({ roomLabel, dateKey }) => {
          const dKey = dateKey || '_no_date_';
          if (!labelsByDate.has(dKey)) labelsByDate.set(dKey, new Set());
          labelsByDate.get(dKey).add(roomLabel);
        });
        const hasSameDateMultiRoom = Array.from(labelsByDate.values()).some((s) => s.size > 1);

        if (hasSameDateMultiRoom || entry.rooms.length > 2) {
          const uniqueLabels = [];
          const seen = new Set();
          (entry.roomItems || []).forEach(({ roomLabel }) => {
            if (!seen.has(roomLabel)) {
              seen.add(roomLabel);
              uniqueLabels.push(roomLabel);
            }
          });
          const labels = uniqueLabels.length > 0 ? uniqueLabels : entry.rooms;
          return labels.map((label, index) => ({
            key: `${entry.key}_${index}`,
            label,
          }));
        }

        return [{
          key: entry.key,
          label: entry.rooms.join('->'),
        }];
      })
      .filter((chip) => Boolean(chip.label));

    const upgradeChips = buildUpgradeLinkChips(sortedItems);

    if (chips.length > 0) {
      if (!upgradeChips.length) {
        return chips;
      }

      const existingLabels = new Set(chips.map((chip) => chip?.label).filter(Boolean));
      const uniqueUpgradeChips = upgradeChips.filter((chip) => !existingLabels.has(chip.label));

      const linkEndpoints = new Set();
      const linkTexts = new Set();
      uniqueUpgradeChips.forEach((chip) => {
        const txt = normalizeRoomText(chip?.label);
        if (!txt) return;
        linkTexts.add(txt);
        const parts = txt.split('->').map((p) => normalizeRoomText(p)).filter(Boolean);
        if (parts.length === 2) {
          linkEndpoints.add(parts[0]);
          linkEndpoints.add(parts[1]);
        }
      });

      const filteredBaseChips = chips.filter((chip) => {
        const txt = normalizeRoomText(chip?.label);
        if (!txt) return false;
        if (linkTexts.has(txt)) return false;
        if (!txt.includes('->') && linkEndpoints.has(txt)) return false;
        return true;
      });

      return [...uniqueUpgradeChips, ...filteredBaseChips];
    }

    if (upgradeChips.length > 0) {
      return upgradeChips;
    }
  }

  const allocatedRooms = Array.isArray(booking?.allocated_rooms) ? booking.allocated_rooms : [];
  return allocatedRooms
    .map((room, index) => {
      const label = getRoomLabel(room);
      if (!label) return null;
      return {
        key: room?.room_id || room?.allocated_room_id || `${index}`,
        label,
      };
    })
    .filter(Boolean);
};
